// src/pages/ContactUs.jsx
import React from 'react';
import {
  Grid,
  Box,
  Button,
  Typography,
  Container,
  Paper,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import Meta from '../../components/common/Meta';
import ContactInformation from './components/ContactInformation';
import ContactForm from './components/ContactForm';
import GoogleMap from './components/GoogleMap';
//import FacebookPage from '../components/FacebookPage';
///import YouTubeEmbed from '../components/YouTubeEmbed';
///import InstagramEmbed from '../components/InstagramEmbed';

const ContactUs = () => {
  // Media query hook for responsive design - detects mobile screens (width <= 600px)
  const isMobile = useMediaQuery('(maxWidth:600px)');

  // State for snackbar notifications (toast messages)
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success' // Can be: 'success', 'error', 'warning', 'info'
  });

  // State for consent dialog - stores form data temporarily until user confirms sharing
  const [consentDialog, setConsentDialog] = React.useState({
    open: false,
    formData: null // Stores the form data while waiting for user consent
  });

  /**
   * Displays a snackbar notification to the user
   * @param {string} message - The message to display
   * @param {string} severity - The type of alert: 'success', 'error', 'warning', 'info'
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Handles form submission from ContactForm component
   * Opens consent dialog instead of immediately submitting to WhatsApp
   * @param {Object} formData - Form data containing name, email, phone, and message
   */
  const handleSubmit = async (formData) => {
    // Store form data and open consent dialog for user confirmation
    // This adds privacy protection by requiring explicit user consent
    setConsentDialog({ open: true, formData });
  };

  /**
   * Handles user confirmation in consent dialog - proceeds with WhatsApp submission
   */
  const handleConsentConfirm = () => {
    const { formData } = consentDialog;
    submitToWhatsApp(formData);
    // Close dialog and clear stored form data
    setConsentDialog({ open: false, formData: null });
  };

  /**
   * Handles user cancellation in consent dialog - cancels the submission process
   */
  const handleConsentCancel = () => {
    // Close dialog and clear stored form data without submission
    setConsentDialog({ open: false, formData: null });
    showSnackbar('Message cancelled. Your data was not shared.', 'info');
  };

  /**
   * Submits form data to WhatsApp by opening a new window with pre-filled message
   * @param {Object} data - Form data containing contact information and message
   */
  const submitToWhatsApp = (data) => {
    // WhatsApp business number (with country code, without + sign)
    const phoneNumber = '923425262121';

    // Format message for WhatsApp with clear structure and bold formatting using asterisks
    const message = `New Contact Request — Noor-un-Nikah Website

Name: *${data.name || 'Not provided'}*
Email: *${data.email || 'Not provided'}*
Phone: *${data.phone || 'Not provided'}*

Message:
*${data.message || 'Not provided'}*
--------------------`;

    // Create WhatsApp API URL with encoded message (spaces and special characters)
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;

    // Open WhatsApp in new tab - this may trigger popup blockers
    const newWindow = window.open(whatsappUrl, '_blank');

    // Handle popup blocker scenarios and provide user feedback
    if (newWindow) {
      showSnackbar('WhatsApp opened! Please review and click send to complete your message.', 'info');
    } else {
      showSnackbar('Please allow popups for WhatsApp to open.', 'warning');
    }
  };

  /**
   * Closes the snackbar notification
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Meta page="contactUs" />
      <Box
        sx={{
          minHeight: '100vh', // Ensure full viewport height
          py: 4, // Vertical padding: 6 * 8px = 48px
        }}
      >
        <Container maxWidth="lg">
          {/* Main paper container for entire contact section with shadow and rounded corners */}
          <Paper
            sx={{
              borderRadius: 2, // Consistent rounded corners
              overflow: 'hidden', // Prevent child content from overflowing rounded corners
            }}
          >
            <Box sx={{ p: { xs: 3, md: 5 } }}> {/* Responsive padding: 24px mobile, 40px desktop */}

              {/* Page title with decorative underline */}
              <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                  mb: 4,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '4px',
                    borderRadius: '2px',
                    // Note: Background color should be defined by theme
                  },
                }}
              >
                Contact Us
              </Typography>

              {/* Main content grid - responsive layout that reverses on mobile */}
              <Grid
                container
                spacing={4} // 32px gap between grid items
                sx={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}
              // On mobile: Contact info appears below form for better UX
              // On desktop: Contact info on left, form on right
              >

                {/* Contact Information Section - Left side on desktop, bottom on mobile */}
                <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                  <Paper
                    sx={{
                      p: 3, // 24px padding
                      flexGrow: 1, // Take full available height
                      borderRadius: 2,
                      transition: 'transform 0.3s, box-shadow 0.3s', // Smooth hover animation
                      '&:hover': {
                        transform: 'translateY(-5px)', // Lift effect on hover
                        // Box shadow would be automatically applied by Material-UI
                      },
                    }}
                  >
                    <ContactInformation />
                  </Paper>
                </Grid>

                {/* Contact Form Section - Right side on desktop, top on mobile */}
                <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
                  <Paper
                    sx={{
                      p: 3,
                      flexGrow: 1,
                      borderRadius: 2,
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <ContactForm onSubmit={handleSubmit} />

                    {/* User instructions box - important information about WhatsApp process */}
                    <Box
                      sx={{
                        mt: 2, // Margin top: 16px
                        p: 2, // Padding: 16px
                        borderRadius: 1, // Slightly smaller border radius
                        border: 1, // Border width 1px - color from theme
                      }}
                    >
                      <Typography variant="body2" align="center">
                        💡 <strong>Important:</strong> After submitting, you'll be asked for confirmation before opening WhatsApp.
                        You'll need to manually click the send button in WhatsApp to complete the process.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Google Map Section - Consistent styling with other sections End */}
              <Box sx={{ mt: 4, display: 'flex' }}>
                <Paper
                  sx={{
                    p: 0, // No internal padding - map should take full space
                    flexGrow: 1,
                    borderRadius: 2,
                    overflow: 'hidden', // Essential for map to respect rounded corners
                    height: '400px', // Fixed height for consistent layout
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)', // Consistent hover effect
                    },
                  }}
                >
                  <GoogleMap />
                </Paper>
              </Box>

              {/* Google Map Section - Consistent styling with other sections End */}


            </Box>
          </Paper>

          {/* Consent Confirmation Dialog - Privacy protection layer */}
          <Dialog
            open={consentDialog.open}
            onClose={handleConsentCancel} // Allow closing via backdrop click or ESC
            aria-labelledby="consent-dialog-title"
          >
            <DialogTitle id="consent-dialog-title">
              Confirm WhatsApp Sharing
            </DialogTitle>
            <DialogContent>
              <Typography>
                You're about to share your contact information via WhatsApp. This will open WhatsApp with your pre-filled message.
                Please confirm that you want to proceed.
              </Typography>
              {/* Additional instructions for user */}
              <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> You'll need to manually click the send button in WhatsApp to complete the message delivery.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  onClick={handleConsentCancel}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={handleConsentConfirm}
                  autoFocus
                  endIcon={<SendIcon />}
                >
                  Open WhatsApp
                </Button>
              </Box>
            </DialogActions>

          </Dialog>

          {/* Snackbar for user notifications (toast messages) */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000} // Auto hide after 6 seconds
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // Position at bottom center
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity} // Dynamic severity based on message type
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default ContactUs;